import { useEffect } from 'react';

/**
 * Popup trascinabili col mouse, solo su desktop, in TUTTE le app senza
 * toccare i singoli modali (~100, quasi tutti `fixed inset-0` fatti a mano).
 *
 * Un listener globale riconosce il pannello di un popup — figlio di un
 * overlay `fixed` a tutto schermo non opaco, oppure un role="dialog" Radix —
 * e lo sposta quando si trascina la testata (primo figlio visibile del
 * pannello, o i primi 48px). Bottoni, campi e link della testata restano
 * cliccabili; sotto i 5px di movimento è un clic normale.
 *
 * Spostamento: `left/top` relativi sui pannelli in flusso, perché un
 * `transform` farebbe da contenitore ai sotto-popup `fixed inset-0` annidati
 * nel pannello (qui non si usano portal) e li ritaglierebbe; `translate` sui
 * pannelli `fixed` dei Dialog Radix, che si somma al loro transform di
 * centratura. Alla chiusura il popup si riapre centrato (è un nodo nuovo).
 */

const MIN_LARGHEZZA_DESKTOP = 768; // md di Tailwind: sotto, i popup sono bottom-sheet
const TESTATA_MIN_PX = 48;
const TESTATA_MAX_PX = 160;
const SOGLIA_PX = 5; // sotto è un clic (anche con la mano che trema), non un trascinamento
const VISIBILE_PX = 120; // quanto pannello resta sempre a schermo, per poterlo riprendere
const INTERATTIVI = 'button, a, input, textarea, select, option, label, summary, video, canvas, iframe, '
  + '[role="button"], [role="tab"], [role="menuitem"], [role="combobox"], [role="checkbox"], '
  + '[role="switch"], [role="slider"], [contenteditable=""], [contenteditable="true"], [data-no-drag]';
// popover/select/menu Radix (role="dialog" anche loro), drawer vaul, toast
const ESCLUSI = '[data-radix-popper-content-wrapper], [vaul-drawer], [data-sonner-toaster]';

const spostamenti = new WeakMap(); // pannello -> { x, y, modo }

function alfa(colore) {
  if (!colore || colore === 'transparent') return 0;
  const m = colore.match(/rgba?\(([^)]+)\)/);
  if (!m) return 1;
  const parti = m[1].split(/[\s,/]+/).filter(Boolean);
  return parti.length >= 4 ? parseFloat(parti[3]) : 1;
}

// Pannello del popup che contiene `el`, o null. Si risale fino al primo
// antenato `fixed`: un Dialog Radix (role="dialog") è già il pannello; un
// overlay fatto a mano (quasi tutto schermo, non opaco: le pagine
// `fixed inset-0 bg-white` come Checkpoint o lo scanner non sono popup) ha
// come pannello il nodo più esterno che non lo riempie. Niente antenato
// fixed = pagina normale. Non si pretende che l'overlay sia esattamente lo
// schermo: un antenato con transform (es. nel Credit) lo sposta di qualche px.
function trovaPannello(el) {
  if (!(el instanceof Element) || el.closest(ESCLUSI)) return null;
  const percorso = [];
  let n = el;
  for (; n && n !== document.body; n = n.parentElement) {
    if (getComputedStyle(n).position === 'fixed') break;
    percorso.push(n);
  }
  if (!n || n === document.body) return null;
  let pannello;
  const ruolo = n.getAttribute('role');
  if (ruolo === 'dialog' || ruolo === 'alertdialog') pannello = n;
  else {
    const ro = n.getBoundingClientRect();
    const overlay = ro.width >= window.innerWidth * 0.9 && ro.height >= window.innerHeight * 0.8;
    if (!overlay || alfa(getComputedStyle(n).backgroundColor) >= 1) return null; // barre fisse, pagine piene
    pannello = percorso.reverse().find((p) => {
      const r = p.getBoundingClientRect();
      return r.width < ro.width - 2 || r.height < ro.height - 2;
    });
    if (!pannello) return null;
  }
  const r = pannello.getBoundingClientRect();
  // a tutta altezza o larghezza = drawer laterale o barra, non una finestra
  if (r.height >= window.innerHeight - 2 || r.width >= window.innerWidth - 2) return null;
  if (alfa(getComputedStyle(pannello).backgroundColor) === 0) return null; // foto dei lightbox
  return pannello;
}

function inTestata(pannello, y) {
  const r = pannello.getBoundingClientRect();
  let fondo = r.top + TESTATA_MIN_PX;
  const primo = Array.from(pannello.children).find((c) => {
    const h = c.getBoundingClientRect().height;
    return h > 0 && getComputedStyle(c).position !== 'absolute';
  });
  if (primo) {
    const rp = primo.getBoundingClientRect();
    if (rp.height <= TESTATA_MAX_PX && rp.height < r.height / 2) fondo = Math.max(fondo, rp.bottom);
  }
  return y >= r.top && y <= fondo;
}

function interattivo(el, pannello) {
  const hit = el.closest(INTERATTIVI);
  if (hit && pannello.contains(hit)) return true;
  // div cliccabili: la manina li tradisce (se non è ereditata dall'overlay)
  const fuori = pannello.parentElement ? getComputedStyle(pannello.parentElement).cursor : '';
  return getComputedStyle(el).cursor === 'pointer' && fuori !== 'pointer';
}

function applica(pannello, s) {
  if (s.modo === 'translate') pannello.style.translate = `${s.x}px ${s.y}px`;
  else {
    pannello.style.left = `${s.x}px`;
    pannello.style.top = `${s.y}px`;
  }
}

function modoPer(pannello) {
  const pos = getComputedStyle(pannello).position;
  if (pos === 'static') pannello.style.position = 'relative';
  return pos === 'static' || pos === 'relative' ? 'offset' : 'translate';
}

function bloccaEvento(e) {
  e.preventDefault();
  e.stopPropagation();
}

export default function ModaliTrascinabili() {
  useEffect(() => {
    let drag = null;
    let cursorePannello = null;
    let ultimoTarget = null;
    let ultimoPannello = null;
    let hoverEvento = null;
    let raf = 0;

    const fermaDrag = () => {
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerup', onUp, true);
      window.removeEventListener('pointercancel', onUp, true);
      window.removeEventListener('dragstart', bloccaEvento, true);
      if (drag?.attivo) {
        document.body.style.userSelect = drag.userSelectPrima;
        drag.pannello.style.transition = drag.transitionPrima;
      }
    };

    function onMove(e) {
      const r = drag.r0;
      const dx = Math.min(Math.max(e.clientX - drag.x0, VISIBILE_PX - r.right), window.innerWidth - VISIBILE_PX - r.left);
      const dy = Math.min(Math.max(e.clientY - drag.y0, -r.top), window.innerHeight - TESTATA_MIN_PX - r.top);
      if (!drag.attivo) {
        if (Math.abs(dx) < SOGLIA_PX && Math.abs(dy) < SOGLIA_PX) return;
        drag.attivo = true;
        // position: relative solo a trascinamento iniziato, non a ogni clic sulla testata
        drag.base = drag.base || { x: 0, y: 0, modo: modoPer(drag.pannello) };
        drag.userSelectPrima = document.body.style.userSelect;
        drag.transitionPrima = drag.pannello.style.transition;
        document.body.style.userSelect = 'none';
        drag.pannello.style.transition = 'none'; // Radix ha duration-200: il pannello inseguirebbe il mouse
      }
      const sel = window.getSelection();
      if (sel && !sel.isCollapsed) sel.removeAllRanges();
      const s = { ...drag.base, x: drag.base.x + dx, y: drag.base.y + dy };
      spostamenti.set(drag.pannello, s);
      applica(drag.pannello, s);
    }

    function onUp() {
      const eraAttivo = drag?.attivo;
      fermaDrag();
      drag = null;
      if (!eraAttivo) return;
      // il rilascio fuori dal pannello farebbe un click sull'overlay = chiusura
      window.addEventListener('click', bloccaEvento, { capture: true, once: true });
      setTimeout(() => window.removeEventListener('click', bloccaEvento, true), 0);
    }

    function onDown(e) {
      if (e.pointerType !== 'mouse' || e.button !== 0 || drag) return;
      if (window.innerWidth < MIN_LARGHEZZA_DESKTOP) return;
      const pannello = trovaPannello(e.target);
      if (!pannello || !inTestata(pannello, e.clientY) || interattivo(e.target, pannello)) return;
      drag = {
        pannello, base: spostamenti.get(pannello), r0: pannello.getBoundingClientRect(),
        x0: e.clientX, y0: e.clientY, attivo: false,
      };
      window.addEventListener('pointermove', onMove, true);
      window.addEventListener('pointerup', onUp, true);
      window.addEventListener('pointercancel', onUp, true);
      window.addEventListener('dragstart', bloccaEvento, true); // foto nella testata
    }

    // cursore «sposta» sulla testata, ricalcolato al massimo una volta per frame
    function aggiornaCursore() {
      raf = 0;
      const e = hoverEvento;
      if (e.target !== ultimoTarget) {
        ultimoTarget = e.target;
        ultimoPannello = window.innerWidth >= MIN_LARGHEZZA_DESKTOP ? trovaPannello(e.target) : null;
      }
      const p = ultimoPannello;
      const sopra = p && p.isConnected && inTestata(p, e.clientY) && !interattivo(e.target, p);
      if (cursorePannello && cursorePannello !== p) cursorePannello.style.cursor = '';
      cursorePannello = p && p.isConnected ? p : null;
      if (cursorePannello) cursorePannello.style.cursor = sopra ? 'move' : '';
    }

    function onHover(e) {
      if (drag || e.pointerType !== 'mouse') return;
      hoverEvento = e;
      if (!raf) raf = requestAnimationFrame(aggiornaCursore);
    }

    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('pointermove', onHover, { passive: true });
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('pointermove', onHover);
      if (raf) cancelAnimationFrame(raf);
      fermaDrag();
    };
  }, []);

  return null;
}
